import { Phone, MessageCircle, Clock, MapPin } from "lucide-react";
import { PHONE, PHONE_TEL, WHATSAPP_URL } from "../data";
import { Reveal } from "./Reveal";
import { EnquiryForm } from "./EnquiryForm";

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, placeholder: "Your full name", testid: "contact-form-name" },
  { name: "phone", label: "Phone", type: "tel", required: true, placeholder: "10-digit mobile number", testid: "contact-form-phone" },
  {
    name: "service_required",
    label: "Service Required",
    type: "select",
    required: true,
    options: ["Marriage Hall", "Hotel Room", "Restaurant", "General Enquiry"],
    testid: "contact-form-service-select",
  },
  { name: "message", label: "Message", type: "textarea", placeholder: "How can we help you?", testid: "contact-form-message" },
];

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-white py-16 sm:py-20 lg:py-28">
    <div className="section-wrap">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">Contact Us</p>
        <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
          We Would Love to Hear From You
        </h2>
      </Reveal>

      <div className="mt-12 grid gap-10 lg:grid-cols-2 lg:gap-14">
        <Reveal>
          <h3 className="font-serif text-2xl font-semibold text-navy">Kasdol Hotel</h3>
          <p className="mt-2 text-sm text-ink/60">Marriage Hall • Hotel Rooms • Restaurant</p>

          <div className="mt-8 space-y-6">
            <a href={PHONE_TEL} data-testid="contact-phone-link" className="group flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-gold/30 bg-gold/10">
                <Phone className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink/50">Call Us</span>
                <span className="mt-0.5 block text-lg font-semibold text-navy transition-colors group-hover:text-gold">
                  {PHONE}
                </span>
              </span>
            </a>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="group flex items-start gap-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-gold/30 bg-gold/10">
                <MessageCircle className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink/50">WhatsApp Us</span>
                <span className="mt-0.5 block text-lg font-semibold text-navy transition-colors group-hover:text-gold">
                  {PHONE}
                </span>
              </span>
            </a>

            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-gold/30 bg-gold/10">
                <Clock className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink/50">Opening Hours</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-ink/75">
                  Front Desk: 24 × 7
                  <br />
                  Restaurant: 7:00 AM – 11:00 PM
                </span>
              </span>
            </div>

            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-gold/30 bg-gold/10">
                <MapPin className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink/50">Location</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-ink/75">
                  Kasdol Hotel — conveniently located and easy to reach. Call us for directions.
                </span>
              </span>
            </div>
          </div>

          <div
            data-testid="contact-map-placeholder"
            className="mt-8 flex aspect-video flex-col items-center justify-center rounded-md border border-gold/20 bg-navy text-center"
          >
            <MapPin className="h-10 w-10 text-gold" />
            <p className="mt-3 font-serif text-xl font-semibold text-cream">Kasdol Hotel</p>
            <p className="mt-1 text-xs text-cream/60">Marriage Hall • Rooms • Restaurant</p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Kasdol%20Hotel"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="mt-4 text-xs font-semibold uppercase tracking-widest text-gold underline-offset-4 hover:underline"
            >
              Find us on Google Maps
            </a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="rounded-md bg-ivory p-6 shadow-xl sm:p-10">
            <h3 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">Send an Enquiry</h3>
            <p className="mt-2 text-sm text-ink/60">We usually respond within a few hours.</p>
            <div className="mt-7">
              <EnquiryForm
                formTestId="contact-general-form"
                submitTestId="contact-form-submit"
                fields={FIELDS}
                submitLabel="Send Enquiry"
                service="general"
                serviceField="service_required"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
